import { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { BottomTabBar } from "@/components/BottomTabBar";
import { DailyVerseModal } from "@/components/DailyVerseModal";
import { BroadcastModal } from "@/components/BroadcastModal";
import { MissedNotificationBanner } from "@/components/MissedNotificationBanner";
import { recordTap, markConsumed } from "@/lib/notificationRecovery";

export function PublicShell() {
  const location = useLocation();

  // Whenever the URL carries the SW-appended ``?nt=1`` notification-tap
  // marker, snapshot it into the recovery record. This runs BEFORE any
  // page-level useEffect so the snapshot is safely persisted even if the
  // page's own deep-link handler strips the ``?nt=1`` (and other query
  // params) right after mount. The classifier inside ``recordTap``
  // filters out untracked / generic taps automatically.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.get("nt")) return;
    recordTap(location.pathname, location.search);
  }, [location.pathname, location.search]);

  // Auto-consume for destinations that ARE the content itself — /events
  // and /prayers don't have a modal to open, the page IS the target.
  // We give the user a 3-second dwell so a quick bounce-back doesn't
  // silently discard the recovery banner they may still need.
  useEffect(() => {
    const isEventOrPrayer =
      location.pathname === "/events"
      || location.pathname.startsWith("/events/")
      || location.pathname === "/prayers"
      || location.pathname.startsWith("/prayers/");
    if (!isEventOrPrayer) return undefined;
    const expected = location.pathname.startsWith("/prayers") ? "prayer" : "event";
    const id = setTimeout(() => markConsumed(expected), 3000);
    return () => clearTimeout(id);
  }, [location.pathname]);

  // IMPORTANT: BottomTabBar MUST live OUTSIDE the `.noise` container.
  // The global rule `.noise > * { position: relative; z-index: 1; }` would
  // otherwise override the tab bar's `position: fixed` (same specificity,
  // later in stylesheet) and the tabs would scroll with the document
  // instead of being pinned to the viewport bottom.
  return (
    <div className="App min-h-screen bg-background">
      <div className="noise">
        <div className="mx-auto max-w-md">
          <main className="pb-safe-tabs">
            <Outlet />
          </main>
        </div>
      </div>
      <BottomTabBar />
      {/* Verse-of-the-day modal: appears once per UTC date on any public page. */}
      <DailyVerseModal />
      {/* In-app pop-up for tapped broadcast notifications (reads ?broadcast=<id>). */}
      <BroadcastModal />
      {/* Recovery prompt for notifications tapped but never consumed. */}
      <MissedNotificationBanner />
    </div>
  );
}
